<script setup lang="ts">
import { ref, onMounted } from "vue";
import moment from "moment";

//pinia
import Store from "@store/dataProvider";
import { storeToRefs } from "pinia";

// composables
import { useToast } from "../../../composables/useToast";
import useAuthStore from "@store/auth";

// types
import { IAgents } from "./../../../types/agents.type";
import { IClient } from "./../../../types/client.type";
import { IUser } from "../../../types/user.type";
import { ITemplateUI } from "../../../types/templatesUI.type";
import { ILanguage } from "../../../types/languages.type";
import { ICompany } from "../../../types/company.type";
import { ICountry } from "../../../types/countries.type";
import { IRole } from "../../../types/user.type";
import { ITypeUser } from "../../../types/typeUsers.type";
import { IReinsurer } from "src/types/reinsurer.type";


//repositories
import TemplatesUIDataProviderRepository from "../../../repositories/templatesUI.repository";
import UserApi from "@repositories/user.repository";
import TypeUsersRepository from "@repositories/typeUsers.repository";
import AgentsDataProviderRepository from "@repositories/agents.repository";
import LanguagesRepository from "@repositories/languages.repository";
import CountriesRepository from "@repositories/countries.repository";
import ClientsDataProviderRepository from "@repositories/clients.repository";
import ReinsurersRepository from '@repositories/reinsurers.repository';

//components
import ModalCreateUser from "./ModalCreateUser.vue";
import ModalUpdateUser from "./ModalUpdateUser.vue";
import { useI18n } from "vue-i18n";
import CompaniesRepository from "@repositories/companies.repository";
import DataTable from "@components/DataTable.vue";

const isLoadingUsers = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { users }: any = storeToRefs(store);

const { user } = useAuthStore();
const { $toast } = useToast();

const agentsProviderController = new AgentsDataProviderRepository();
const templatesProviderController = new TemplatesUIDataProviderRepository();
const clientsProviderController = new ClientsDataProviderRepository();

const userRepository = new UserApi();
const usersData = ref<IUser[]>([]);
const countriesData = ref<ICountry[]>([]);
const rolesData = ref<IRole[]>([]);
const companiesData = ref<Array<ICompany>>([]);
const languagesData = ref<ILanguage[]>([]);
const agentsData = ref<Array<IAgents>>([]);
const clientsData = ref<Array<IClient>>([]);
const reinsurersData = ref<Array<IReinsurer>>([]);
const templatesUIData = ref<Array<ITemplateUI>>([]);
const typeUsersData = ref<Array<ITypeUser>>([]);
const paginationData = ref();
const currentPage = ref<number>();
const isModalOpen = ref<boolean>(false);
const modalType = ref<string | null>(null);
const modalTitle = ref<string>();
const selectedUserInfo = ref<IUser | null>(null);
const filterData = ref<any>(null);
const searchFormRef = ref<any>(null);

const getTypeUsersAPI = async () => {
  const data = await TypeUsersRepository.getTypeUsers();
  typeUsersData.value = data || [];
};

const getTemplatesUIAPI = async () => {
  const response = await templatesProviderController.getTemplates();
  if (!response) return;
  if (Array.isArray(response?.data)) {
    templatesUIData.value = response.data;
  } else {
    templatesUIData.value = [];
  }
};
const getAgentsAPI = async () => {
  const response = await agentsProviderController.indexAgents();
  if (!response) return;
  if (Array.isArray(response?.data)) {
    agentsData.value = response.data;
  } else {
    agentsData.value = [];
  }
};

const getClientsAPI = async () => {
  const response = await clientsProviderController.indexClient();
  if (!response) return;
  if (Array.isArray(response?.data)) {
    clientsData.value = response.data;
  } else {
    clientsData.value = [];
  }
};

const getReinsurersAPI = async () => {
  const response = await ReinsurersRepository.getReinsurers();
  if (!response) return;
  if (Array.isArray(response?.reinsurers)) {
    reinsurersData.value = response.reinsurers;
  } else {
    reinsurersData.value = [];
  }
};

const getUsersAPI = async (page: number) => {
  isLoadingUsers.value = true;
  try {
    const data = await userRepository.getUsers({
      page,
      searchData: filterData.value,
    });
    if (!data) return;
    if (Array.isArray(data?.users)) {
      usersData.value = data.users;
      paginationData.value = data.pagination;
      store.setProp({ key: "users", value: usersData.value });
    } else {
      usersData.value = [];
    }
  } finally {
    isLoadingUsers.value = false;
  }
};

const getLanguagesAPI = async (page: number) => {
  const { data } = await LanguagesRepository.getlanguages({
    page,
  });

  if (!data) return;
  if (Array.isArray(data?.languages)) {
    languagesData.value = data.languages;
    store.setProp({ key: "languages", value: usersData.value });
  } else {
    languagesData.value = [];
  }
};

const getCompaniesAPI = async () => {
  const { companies } = await CompaniesRepository.getCompanies(true);
  if (Array.isArray(companies)) {
    companiesData.value = companies.map((e: ICompany) => ({
      ...e,
      nombre: e.name,
    }));

    store.setProp({ key: "companies", value: companiesData.value });
  }
};

const getCountriesFromApi = async () => {
  const data = await CountriesRepository.getAllcountries();
  countriesData.value = data.data;
};

const getRolesFromApi = async () => {
  const data = await userRepository.getRoles();
  rolesData.value = data;
};

onMounted(async () => {
  getUsersAPI(1);
  getAgentsAPI();
  getClientsAPI();
  getReinsurersAPI();
  getTemplatesUIAPI();
  getLanguagesAPI(1);
  getCompaniesAPI();
  getCountriesFromApi();
  getRolesFromApi();
  getTypeUsersAPI();
});

const fieldsTable: any = ref([
  {
    key: "id",
    label: "Id",
    sortable: true,
  },
  {
    key: "email",
    label: t("global.email"),
  },
  {
    key: "fullName",
    label: t("global.nombre"),
  },
  {
    key: "company",
    label: t("global.compania"),
    formatter: (e: any) => e?.name || null,
  },
  {
    key: "countries",
    label: t("global.paises"),
    formatter: (e: any) => e?.map((e: any) => e.description).join(',') || null,
  },
  {
    key: "rolesDescriptions",
    label: t("ajustes.ajustes.roles"),
    formatter: (e: any) => e?.join(',') || null,
  },
  {
    key: "createdAt",
    label: t("global.fechayhora"),
    formatter: (e: any) => moment(e).format("DD-MM-YYYY HH:mm"),
  },
  {
    key: "language",
    label: t("ajustes.usuarios.usuarios.idioma"),
    formatter: (e: any) => e?.language || null,
  },
  {
    key: "userInfo",
    label: t("global.editar"),
  },
  {
    key: "statusBlock",
    label: t("global.estado"),
  },
]);

const onChangePage = async (page: any) => {
  getUsersAPI(page);
  currentPage.value = page;
};

const handleOpenModalUsers = () => {
  modalTitle.value = t("ajustes.usuarios.usuarios.crear_usuario");
  isModalOpen.value = true;
  modalType.value = "modalAddUsers";
};
const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
};

const handleSelectClient = (userInfo: any) => {
  modalTitle.value = t("ajustes.usuarios.usuarios.actualizar_usuario");
  (isModalOpen.value = true), (modalType.value = "modalUpdateUser");
  selectedUserInfo.value = userInfo;
};

const handleChangeStatusUser = async (e: any) => {
  if (e.id === user?.id) {
    return $toast?.error(
      t("ajustes.usuarios.usuarios.no_puedes_autobloquearte")
    );
  }

  if (!e.deletedAt) {
    try {
      const { data } = await userRepository.blockUser(e.id);
      if (!data) throw new Error();
      let index = store.users.findIndex((value: any) => value.id === data.id);
      if (index > -1) {
        store.setProp({ key: "users", index, value: data });
      }

      $toast?.success(
        t("ajustes.usuarios.usuarios.usuario_bloqueado_con_exito")
      );
      return;
    } catch (error) {
      $toast?.error(t("global.ocurrio_un_error"));
    }
    return;
  }
  try {
    const { data } = await userRepository.unBlockUser(e.id);
    if (!data) throw new Error();
    let index = store.users.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
      store.setProp({ key: "users", index, value: data });
    }

    $toast?.success(
      t("ajustes.usuarios.usuarios.usuario_desbloqueado_con_exito")
    );
  } catch (error) {
    $toast?.error(t("global.ocurrio_un_error"));
  }
};

const fieldsFilter: any = ref([
  {
    key: "search",
    title: "Buscar",
    type: "text",
    cols: 6,
  },
  {
    key: "companyId",
    title: "Sponsor",
    type: "select",
    trackBy: "id",
    searchable: true,
    options: companiesData,
    placeholder: "Filtrar por sponsor",
    cols: 6,
    formatValue: (val: any) => val?.id ?? null,
  },
  {
    key: "roleId",
    title: "Rol",
    selectLabel: null,
    label: "title",
    type: "select",
    placeholder: "Filtrar por rol",
    cols: 6,
    options: rolesData,
    formatValue: (val: any) => val?.id ?? null,
  },
  {
    key: "countryId",
    label: "description",
    selectLabel: null,
    searchable: true,
    title: "Pais",
    trackBy: "id",
    type: "select",
    placeholder: "Filtrar por pais",
    cols: 6,
    options: countriesData,
    formatValue: (val: any) => val?.id ?? null,
  },
]);

const handleSearch = async ({ items }: any) => {
  filterData.value = items;
  getUsersAPI(1);
};

const resetSearch = async () => {
  filterData.value = null;
  await searchFormRef.value.resetValues();
  getUsersAPI(1);
};
</script>

<template>
  <Layout
    :title="t('ajustes.usuarios.usuarios.usuarios')"
    :textColor="'color-secondary'"
  >
    <template #layout>
      <div class="d-flex gap-2 align-items-center">
        <Form
          @onSubmit="handleSearch"
          :inputs="fieldsFilter"
          ref="searchFormRef"
          customClass="poliza__form w-100"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
            >
              <template #content> Buscar </template>
            </CButton>
            <!-- @click="handleReset" -->
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="resetSearch"
            >
              <template #content> Limpiar filtro </template>
            </CButton>
          </template>
        </Form>
      </div>
      <div class="d-flex justify-content-end">
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalUsers"
        >
          <template #content>{{
            t("ajustes.usuarios.usuarios.crear_usuario")
          }}</template>
        </CButton>
      </div>
      <DataTable
        :loading="isLoadingUsers"
        :fields="fieldsTable"
        :items="users"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-if="true" v-slot:cell(userInfo)="operationsData">
          <div
            @click="handleSelectClient(operationsData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
        <template v-if="true" v-slot:cell(statusBlock)="userData">
          <div>
            <img
              @click="handleChangeStatusUser(userData.data.item)"
              class="cursor-pointer"
              :src="
                userData.data.item.deletedAt
                  ? '/icons/user_panel_x.svg'
                  : '/icons/user_panel.svg'
              "
              style="width: 40px"
            />
          </div>
        </template>
      </DataTable>
      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
        size="xl"
      >
        <template v-if="modalType === 'modalAddUsers'">
          <ModalCreateUser
            v-if="agentsData && templatesUIData"
            @closeModal="handleClose"
            :clients="clientsData"
            :reinsurers="reinsurersData"
            :agents="agentsData"
            :companies="companiesData"
            :templatesUI="templatesUIData"
            :languages="languagesData"
            :typeUsers="typeUsersData"
          />
        </template>
        <template v-if="modalType === 'modalUpdateUser'">
          <ModalUpdateUser
            v-if="selectedUserInfo && agentsData && templatesUIData"
            @closeModal="handleClose"
            :userData="selectedUserInfo"
            :clients="clientsData"
            :reinsurers="reinsurersData"
            :agents="agentsData"
            :companies="companiesData"
            :templatesUI="templatesUIData"
            :languages="languagesData"
            :typeUsers="typeUsersData"
          />
        </template>
      </BModal>
    </template>
  </Layout>
</template>
